/**
 * This file was generated based on the old openapi docs that our old python backend hosted.
 * We used it to help us port the python backend to the new effect-ts backend.
 * 
 * That cutover has now happened, so ideally we should slowly migrate these schemas out of this file and into the packages/core/api-contract/src/schemas/
 * folder as hand rolled schemas.
 * 
 * In some cases we can make the types stricter than the old py generated ones. We may also do not need to use S.Class and we can use Schema.Struct directly.
 */

import type * as HttpClient from "effect/http/HttpClient"
import * as HttpClientError from "effect/http/HttpClientError"
import * as HttpClientRequest from "effect/http/HttpClientRequest"
import * as HttpClientResponse from "effect/http/HttpClientResponse"
import * as Data from "effect/Data"
import * as Effect from "effect/Effect"
import type { SchemaError as ParseError } from "effect/Schema"
import * as S from "effect/Schema"
import * as Option from "effect/Option"
import * as Predicate from "effect/Predicate"
import * as SchemaGetter from "effect/SchemaGetter"

/**
* A subscription created by a user referring a number of friends.
*/
export const ReferralSubscriptionView = S.Struct({
  /**
* End date of the referral subscription
*/
"end_date_ms": S.Int,
  /**
* Start date of the referral subscription
*/
"start_date_ms": S.Int
}).annotate({ identifier: "ReferralSubscriptionView" });
export type ReferralSubscriptionView = typeof ReferralSubscriptionView.Type;

/**
* The interval at which the subscription recurs
*/
export class RevenueCatProductViewDefaultInterval extends S.Literals(["day", "week", "month", "year"]) {}

/**
* Data parsed from RevenueCat product ID.
*/
export const RevenueCatProductView = S.Struct({
  /**
* Recurring cost of the subscription
*/
"default_amount_cents": S.Int,
  /**
* The interval at which the subscription recurs
*/
"default_interval": RevenueCatProductViewDefaultInterval,
  /**
* Duration of the billing cycle, e.g. "3" for a subscription that bills every 3 years
*/
"default_interval_count": S.Int,
  /**
* RevenueCat product ID
*/
"id": S.String
}).annotate({ identifier: "RevenueCatProductView" });
export type RevenueCatProductView = typeof RevenueCatProductView.Type;

/**
* Current status of the subscription, mapped to a corresponding Stripe status type. Note that "canceled" does not mean user is out of credit! Check payment status.
*/
export class RevenueCatSubscriptionViewStatus extends S.Literals(["incomplete", "incomplete_expired", "trialing", "active", "past_due", "canceled", "unpaid"]) {}

export const RevenueCatSubscriptionView = S.Struct({
  /**
* When the user unsubscribed, as an ISO 8601 date string. If present, does not indicate user's current subscription has finished!
*/
"canceled_date": S.NullOr(S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}"))))),
  /**
* URL at which the user can manage their subscription in the App or Play Store
*/
"management_url": S.NullOr(S.String),
  /**
* When the subscription ends, as an ISO 8601 date string.
*/
"next_payment_date": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}")))),
  /**
* Data parsed from RevenueCat product ID
*/
"product": RevenueCatProductView,
  /**
* Current status of the subscription, mapped to a corresponding Stripe status type. Note that "canceled" does not mean user is out of credit! Check payment status.
*/
"status": RevenueCatSubscriptionViewStatus
}).annotate({ identifier: "RevenueCatSubscriptionView" });
export type RevenueCatSubscriptionView = typeof RevenueCatSubscriptionView.Type;

/**
* A payment phase represents the time period between two non-zero invoices to the customer.
* The current phase is the one the user is currently in and has already paid for.
*/
export const StripeCurrentPaymentPhaseView = S.Struct({
  /**
* End date of the current phase. This can be in the past if a user's subscription has ended.
*/
"end_date": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}")))),
  /**
* Is the user in a trial phase?
*/
"is_trial": S.Boolean,
  /**
* Start date of the current phase
*/
"start_date": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}"))))
}).annotate({ identifier: "StripeCurrentPaymentPhaseView" });
export type StripeCurrentPaymentPhaseView = typeof StripeCurrentPaymentPhaseView.Type;

/**
* How often the subscription normally recurs
*/
export class StripeSubscriptionViewDefaultInterval extends S.Literals(["day", "week", "month", "year"]) {}

/**
* A payment phase represents the time period between two non-zero invoices to the customer.
* The "next payment phase" represents the time from the user's next invoice until the subsequent invoice.
* amount_cents denotes the cost of the invoice at the start of the next payment phase - the user's next non-$0 charge.
*/
export const StripeNextPaymentPhaseView = S.Struct({
  /**
* Cost of the next phase, in USD cents. This is what the user will be charged in their next non-zero invoice.
*/
"amount_cents": S.Int,
  /**
* End date of the next payment phase
*/
"end_date": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}")))),
  /**
* Start date of the next payment phase - should be equal to the end date of the current phase.
*/
"start_date": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}"))))
}).annotate({ identifier: "StripeNextPaymentPhaseView" });
export type StripeNextPaymentPhaseView = typeof StripeNextPaymentPhaseView.Type;

/**
* Card details of the payment method attached to the Stripe subscription.
*/
export const StripePaymentMethodCardView = S.Struct({
  /**
* Brand of the card
*/
"brand": S.String,
  /**
* Expiration month of the card
*/
"exp_month": S.Int,
  /**
* Expiration year of the card
*/
"exp_year": S.Int,
  /**
* Last 4 digits of the card number
*/
"last_4": S.String
}).annotate({ identifier: "StripePaymentMethodCardView" });
export type StripePaymentMethodCardView = typeof StripePaymentMethodCardView.Type;

/**
* The payment method attached to the Stripe subscription.
*/
export const StripePaymentMethodView = S.Struct({
  /**
* Card details, if the payment method is a card
*/
"card": S.NullOr(StripePaymentMethodCardView),
  /**
* Date the payment method was created
*/
"created": S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}")))),
  /**
* Type of payment method
*/
"type": S.Literal("card")
}).annotate({ identifier: "StripePaymentMethodView" });
export type StripePaymentMethodView = typeof StripePaymentMethodView.Type;

/**
* Current status of the subscription. Note that "canceled" does not mean user is out of credit!
*/
export class StripeSubscriptionViewStatus extends S.Literals(["incomplete", "incomplete_expired", "trialing", "active", "past_due", "canceled", "unpaid", "paused"]) {}

export const StripeSubscriptionView = S.Struct({
  /**
* Whether the subscription is set to cancel at the end of the current billing period.
* When true, the user still has access but will not be renewed.
*/
"cancel_at_period_end": S.Boolean.pipe(
  S.withDecodingDefaultType(Effect.sync(() => false)),
),
  /**
* When the user unsubscribed, if applicable, as an ISO 8601 date string
*/
"canceled_date": S.NullOr(S.String.pipe(S.check(S.isPattern(new RegExp("\\d{4}-\\d{2}-\\d{2}"))))),
  /**
* Current payment phase
*/
"current_payment_phase": StripeCurrentPaymentPhaseView,
  /**
* Recurring cost of the subscription
*/
"default_amount_cents": S.Int,
  /**
* How often the subscription normally recurs
*/
"default_interval": StripeSubscriptionViewDefaultInterval,
  /**
* Duration of the billing cycle, e.g. "3" for a subscription that bills every 3 years
*/
"default_interval_count": S.Int,
  /**
* Does this subscription have an active schedule from an upsell? Specifically, does it have a schedule that hasn't reached its final phase?
*/
"has_active_schedule": S.Boolean,
  /**
* Used to complete incomplete subscriptions.
*/
"latest_invoice_payment_intent_client_secret": S.NullOr(S.String),
  /**
* Next payment phase, present only if the user's subscription will be renewed
*/
"next_payment_phase": S.NullOr(StripeNextPaymentPhaseView),
  /**
* Payment method attached to the subscription
*/
"payment_method": S.NullOr(StripePaymentMethodView),
  /**
* Price ID of the subscription - does not reflect any extensions.
*/
"price_id": S.String,
  /**
* Current status of the subscription. Note that "canceled" does not mean user is out of credit!
*/
"status": StripeSubscriptionViewStatus
}).annotate({ identifier: "StripeSubscriptionView" });
export type StripeSubscriptionView = typeof StripeSubscriptionView.Type;

/**
* Summary of user's subscriptions across all payment platforms.
*/
export class GetGetSubscriptions200 extends S.Struct({
  /**
* User's subscription data from the referral program
*/
"referral_subscription": S.NullOr(ReferralSubscriptionView).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* User's subscription data from RevenueCat
*/
"revenue_cat_subscription": S.NullOr(RevenueCatSubscriptionView),
  /**
* User's subscription data from Stripe
*/
"stripe_subscription": S.NullOr(StripeSubscriptionView)
}) {}

/**
* 2-letter ISO 639-1 language code: "en" (only English is currently supported)
*/
export class PostTextToPhonemesRequestLang extends S.Literals(["en", "es", "fr", "hi", "it", "ja", "pt", "zh"]) {}

export const PostTextToPhonemesRequest = S.Struct({
  /**
* 2-letter ISO 639-1 language code: "en" (only English is currently supported)
*/
"lang": PostTextToPhonemesRequestLang,
  /**
* The input text to convert to phonemes
*/
"text": S.String,
  /**
* Voice to use for text-to-speech: "Will", "Amy", etc.
*/
"voice": S.optional(S.NullOr(S.String)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.String)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("Will" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}).annotate({ identifier: "PostTextToPhonemesRequest" });
export type PostTextToPhonemesRequest = typeof PostTextToPhonemesRequest.Type;

export class PostTextToPhonemes200 extends S.Struct({
  /**
* The resulting phonemes for some input text
*/
"phonemes": S.String
}) {}

export const PostLoginRequest = S.Struct({
  /**
* The email address of the user
*/
"email": S.String,
  /**
* The FCM token of the users device
*/
"fcm_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The password of the user
*/
"password": S.String
}).annotate({ identifier: "PostLoginRequest" });
export type PostLoginRequest = typeof PostLoginRequest.Type;

export class PostLogin200 extends S.Struct({
  /**
* The JWT token for the user
*/
"Authorization": S.String,
  /**
* The ID of the user
*/
"_id": S.String,
  /**
* The country code from CloudFront geo headers
*/
"country_code": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The email address of the user
*/
"email": S.String,
  /**
* The FCM token of the users device
*/
"fcm_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The firebase custom token for the user
*/
"firebase_custom_token": S.String,
  /**
* The onboarding status of the user
*/
"onboardingStatus": S.NullOr(S.String),
  /**
* The referral code of the user
*/
"referral_code": S.String,
  /**
* The status of the login request
*/
"status": S.String
}) {}

/**
* Response schema for GET requests on /user/referral-list.
*/
export const ReferralListItem = S.Struct({
  /**
* The email address of the referred user
*/
"email": S.String,
  /**
* The first name of the referred user
*/
"first_name": S.NullOr(S.String),
  /**
* The last name of the referred user
*/
"last_name": S.NullOr(S.String)
}).annotate({ identifier: "ReferralListItem" });
export type ReferralListItem = typeof ReferralListItem.Type;

/**
* Response schema for GET requests on /user/referral-list.
*/
export class GetGetReferralList200 extends S.Struct({
  /**
* A list of referred users
*/
"referrals": S.Array(ReferralListItem)
}) {}

export class PostRegisterParams extends S.Struct({
  "random_password": S.optional(S.NullOr(S.Boolean)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Boolean)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (false as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}) {}

export const PostRegisterRequest = S.Struct({
  /**
* The email address of the user
*/
"email": S.String,
  /**
* Facebook click cookie for analytics
*/
"facebook_click_cookie": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Facebook click ID for analytics
*/
"facebook_click_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Facebook pixel cookie for analytics
*/
"facebook_pixel_cookie": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* FCM token of the users device
*/
"fcm_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Firebase ID token for authentication
*/
"firebase_id_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Google click ID for analytics
*/
"google_click_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The password of the user
*/
"password": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The Stripe price ID
*/
"price_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Promotional code
*/
"promo_code": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Number of trial period days
*/
"trial_period_days": S.NullOr(S.Int).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* UTM campaign parameter
*/
"utm_campaign": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* UTM content parameter
*/
"utm_content": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* UTM medium parameter
*/
"utm_medium": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* UTM source parameter
*/
"utm_source": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* UTM term parameter
*/
"utm_term": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "PostRegisterRequest" });
export type PostRegisterRequest = typeof PostRegisterRequest.Type;

export class PostRegister200 extends S.Struct({
  /**
* The JWT token for the user
*/
"Authorization": S.String,
  /**
* The ID of the user
*/
"_id": S.String,
  /**
* Stripe client secret if available
*/
"client_secret": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The country code from CloudFront geo headers
*/
"country_code": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Stripe customer ID if available
*/
"customer_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The email address of the user
*/
"email": S.String,
  /**
* The FCM token of the users device
*/
"fcm_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The firebase custom token for the user
*/
"firebase_custom_token": S.String,
  /**
* Random password if generated
*/
"random_password": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The referral code of the user
*/
"referral_code": S.String,
  /**
* The status of the registration request
*/
"status": S.String
}) {}

export class PostRegister409 extends S.Struct({
  /**
* Error message describing why registration failed
*/
"message": S.String,
  /**
* The status of the registration request, always "failed"
*/
"status": S.String
}) {}

export const PostSetFcmTokenRequest = S.Struct({
  /**
* The FCM token of the users device
*/
"fcm_token": S.String
}).annotate({ identifier: "PostSetFcmTokenRequest" });
export type PostSetFcmTokenRequest = typeof PostSetFcmTokenRequest.Type;

/**
* Request schema for POST requests on /user/set-referral.
*/
export const PostSetReferredByCodeRequest = S.Struct({
  /**
* The referral code
*/
"referral_code": S.String
}).annotate({ identifier: "PostSetReferredByCodeRequest" });
export type PostSetReferredByCodeRequest = typeof PostSetReferredByCodeRequest.Type;

export const UserMetering = S.Struct({
  /**
* Returns true if the user has an active subscription from inviting enough friends.
*/
"has_active_referral_subscription": S.Boolean,
  /**
* Returns true if the user has an active subscription in Stripe. The mobile frontend needs to discriminate between this free trial and a valid stripe subscription.
*/
"has_active_stripe_subscription": S.Boolean,
  /**
* Returns true if the user should have access to the product. The backend will eventually use this to block expired users from using some API endpoints.
*/
"is_active_subscription": S.Boolean
}).annotate({ identifier: "UserMetering" });
export type UserMetering = typeof UserMetering.Type;

export const UserOnboarding = S.Struct({
  /**
* Returns true if the user has completed the onboarding process.
*/
"is_complete": S.Boolean
}).annotate({ identifier: "UserOnboarding" });
export type UserOnboarding = typeof UserOnboarding.Type;

export const UserPreferences = S.Struct({
  /**
* The user's preferred voice for TTS ("Any", "Dan", etc.)
*/
"voice": S.String
}).annotate({ identifier: "UserPreferences" });
export type UserPreferences = typeof UserPreferences.Type;

export const UserProfile = S.Struct({
  /**
* The first name of the user, if available
*/
"first_name": S.NullOr(S.String),
  /**
* The first name of the user, if available
*/
"last_name": S.NullOr(S.String)
}).annotate({ identifier: "UserProfile" });
export type UserProfile = typeof UserProfile.Type;

export class GetSummary200 extends S.Struct({
  /**
* The country code from CloudFront geo headers
*/
"country_code": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The email address of the user
*/
"email": S.String,
  /**
* A firebase custom token allows the client to headlessly sign in to firebase. This is useful in allowing old clients to use firebase features without having to reauthenticate.
*/
"firebase_custom_token": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The Firebase UID of the user. This is used to identify the user in Firebase.
*/
"firebase_uid": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Mongo-style ObjectId of this User instance
*/
"id": S.String,
  /**
* Metering information for the user
*/
"metering": UserMetering,
  /**
* Onboarding information for the user
*/
"onboarding": UserOnboarding,
  /**
* User-configured preferences (voice, etc.)
*/
"preferences": UserPreferences,
  /**
* User-provided information about themselves (name, etc.)
*/
"profile": UserProfile,
  /**
* The referral code that can be shared with friends to earn rewards.
*/
"referral_code": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The user ID of the user who referred this user.
*/
"referred_by": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}) {}

export class GetListAudioV2ParamsSortBy extends S.Literals(["created_at", "played_at"]) {}

export class GetListAudioV2ParamsStatusEnum extends S.Literals(["initializing", "processingPDF", "completedExtraction", "complete", "deleted", "Error"]) {}

export class GetListAudioV2ParamsExceptStatusEnum extends S.Literals(["initializing", "processingPDF", "completedExtraction", "complete", "deleted", "Error"]) {}

export class GetListAudioV2Params extends S.Struct({
  "cursor": S.optional(S.NullOr(S.String)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.String)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  "sort_by": S.optional(S.NullOr(GetListAudioV2ParamsSortBy)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(GetListAudioV2ParamsSortBy)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("created_at" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  "status": S.NullOr(S.Union([S.Array(GetListAudioV2ParamsStatusEnum),
GetListAudioV2ParamsStatusEnum])).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  "except_status": S.optional(S.NullOr(S.Union([S.Array(GetListAudioV2ParamsExceptStatusEnum),
GetListAudioV2ParamsExceptStatusEnum]))).pipe(
  S.decodeTo(S.optional(S.toType(S.Union([S.Array(GetListAudioV2ParamsExceptStatusEnum),
GetListAudioV2ParamsExceptStatusEnum]))), {
    decode: SchemaGetter.transformOptional(
      (
        value: Option.Option<
          | ReadonlyArray<S.Schema.Type<typeof GetListAudioV2ParamsExceptStatusEnum>>
          | S.Schema.Type<typeof GetListAudioV2ParamsExceptStatusEnum>
          | null
          | undefined
        >,
      ) => value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
),
  "folder_id": S.NullOr(S.Union([S.String,
S.Literal("root")])).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  "if_modified_since_ms": S.NullOr(S.Int).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  "limit": S.optional(S.NullOr(S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(1)), S.check(S.isLessThanOrEqualTo(20))))).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(1)), S.check(S.isLessThanOrEqualTo(20))))), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (20 as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}) {}

/**
* The current status of the conversion
*/
export class AudioConversionSummaryConversionStatus extends S.Literals(["initializing", "processingPDF", "completedExtraction", "complete", "deleted", "Error"]) {}

export class AudioConversionSummaryDocumentTypeEnum extends S.Literals(["HTML", "PDF", "DOC", "DOCX", "PPT", "PPTX", "TXT", "MD", "RTF", "EPUB", "MOBI", "EMAIL", "EMAIL_ATTACHMENT"]) {}

export class TtsCacheVoicesDataStatus extends S.Literals(["pending", "processing", "completed", "failed"]) {}

export const TtsCacheVoicesData = S.Struct({
  "finished_at_ms": S.NullOr(S.Int).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  "started_at_ms": S.Int,
  "status": S.optional(S.NullOr(TtsCacheVoicesDataStatus)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(TtsCacheVoicesDataStatus)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("pending" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  "url": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  "version": S.optional(S.NullOr(S.Int)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Int)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (1413 as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  /**
* The voice that was used to generate the TTS data
*/
"voice": S.String
}).annotate({ identifier: "TtsCacheVoicesData" });
export type TtsCacheVoicesData = typeof TtsCacheVoicesData.Type;

export const AudioConversionFinalizedMetadata = S.Struct({
  "chapter_details_s3_url": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The TTS data for each voice
*/
"tts_finalized_details": S.optional(S.NullOr(S.Array(TtsCacheVoicesData))).pipe(
  S.decodeTo(S.optional(S.toType(S.Array(TtsCacheVoicesData))), {
    decode: SchemaGetter.transformOptional(
      (
        value: Option.Option<
          ReadonlyArray<S.Schema.Type<typeof TtsCacheVoicesData>> | null | undefined
        >,
      ) => value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
)
}).annotate({ identifier: "AudioConversionFinalizedMetadata" });
export type AudioConversionFinalizedMetadata = typeof AudioConversionFinalizedMetadata.Type;

export class AudioConversionSummaryLanguageEnum extends S.Literals(["en", "es", "fr", "hi", "it", "ja", "pt", "zh"]) {}

/**
* The user's last-known chapter/timestamp position for audio playback.
*/
export const AudioConversionProgress = S.Struct({
  /**
* The ID of the user's last-played chapter (defaults to the first chapter)
*/
"chapter_id": S.String,
  /**
* The title of the user's last-played chapter
*/
"chapter_title": S.String,
  /**
* The estimated percent complete of the conversion, null if not available
*/
"estimated_percent_complete": S.Int,
  /**
* The user's last-known timestamp in milliseconds for this chapter
*/
"timestamp_ms": S.Int,
  /**
* The version of the TTS batch used to generate this progress
*/
"tts_batch_version": S.Int,
  /**
* The ttsId containing this position. Null if progress is not using streaming API.
*/
"tts_id": S.NullOr(S.String),
  /**
* When progress was last updated, null if never played
*/
"updated_at_ms": S.NullOr(S.Int)
}).annotate({ identifier: "AudioConversionProgress" });
export type AudioConversionProgress = typeof AudioConversionProgress.Type;

/**
* Details about who has access to a shared conversion.
*/
export const SharedWith = S.Struct({
  /**
* Email addresses with access
*/
"emails": S.optional(S.NullOr(S.Array(S.String))).pipe(
  S.decodeTo(S.optional(S.toType(S.Array(S.String))), {
    decode: SchemaGetter.transformOptional(
      (value: Option.Option<readonly string[] | null | undefined>) =>
        value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
),
  /**
* User IDs with access
*/
"user_ids": S.optional(S.NullOr(S.Array(S.String))).pipe(
  S.decodeTo(S.optional(S.toType(S.Array(S.String))), {
    decode: SchemaGetter.transformOptional(
      (value: Option.Option<readonly string[] | null | undefined>) =>
        value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
)
}).annotate({ identifier: "SharedWith" });
export type SharedWith = typeof SharedWith.Type;

/**
* Reasons why a conversion cannot be shared.
*/
export class UnshareableReason extends S.Literals(["default_conversion", "copyright_risk"]) {}

/**
* The sharing mode for this conversion
*/
export class SharingInfoVisibility extends S.Literals(["private", "public", "selective_share"]) {}

/**
* Information about how a conversion is shared.
*/
export const SharingInfo = S.Struct({
  /**
* Details about who has access
*/
"shared_with": S.optional(S.NullOr(SharedWith)).pipe(
  S.decodeTo(S.optional(S.toType(SharedWith)), {
    decode: SchemaGetter.transformOptional(
      (value: Option.Option<S.Schema.Type<typeof SharedWith> | null | undefined>) =>
        value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
),
  /**
* The reason this conversion is not shareable (if applicable)
*/
"unshareable_reason": S.NullOr(UnshareableReason).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The sharing mode for this conversion
*/
"visibility": S.optional(S.NullOr(SharingInfoVisibility)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(SharingInfoVisibility)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("private" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}).annotate({ identifier: "SharingInfo" });
export type SharingInfo = typeof SharingInfo.Type;

/**
* A user's tag associated with an AudioConversion.
*/
export const AudioConversionTag = S.Struct({
  /**
* The color of the tag (color format is defined by the client)
*/
"color": S.String,
  /**
* The name of the tag
*/
"label": S.String
}).annotate({ identifier: "AudioConversionTag" });
export type AudioConversionTag = typeof AudioConversionTag.Type;

/**
* A minimal representation of a UserPodcastItem and its corresponding AudioConversion.
*/
export const AudioConversionSummary = S.Struct({
  /**
* Unix timestamp (in milliseconds) when this conversion was archived
*/
"archived_at_ms": S.NullOr(S.Int).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The current status of the conversion
*/
"conversion_status": AudioConversionSummaryConversionStatus,
  /**
* The URL of the cover image for this conversion
*/
"cover_img_url": S.NullOr(S.String),
  /**
* Unix timestamp (in milliseconds) when this user added the conversion
*/
"created_at_ms": S.Int,
  /**
* The type of document
*/
"document_type": S.NullOr(AudioConversionSummaryDocumentTypeEnum).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The URL of the source document
*/
"document_url": S.NullOr(S.String),
  /**
* For failed conversions, an optional user-facing error message
*/
"error": S.NullOr(S.String),
  /**
* Metadata about the finalized conversion (if exists)
*/
"finalized_metadata": S.NullOr(AudioConversionFinalizedMetadata).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The ID of the folder containing this conversion (if exists)
*/
"folder_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Absolute path of the parent folder (list is empty if root)
*/
"folder_path": S.Array(S.String),
  /**
* Mongo-style ObjectId of this AudioConversion instance
*/
"id": S.String,
  /**
* Detected language of the document
*/
"language": S.NullOr(AudioConversionSummaryLanguageEnum),
  /**
* Mongo-style ObjectId of the user who owns this conversion
*/
"owner_id": S.String,
  /**
* The number of pages in the document
*/
"page_count": S.NullOr(S.Int),
  /**
* Latest position, or null for pending conversion
*/
"progress": S.NullOr(AudioConversionProgress),
  /**
* Information about how this conversion is shared
*/
"sharing": S.optional(S.NullOr(SharingInfo)).pipe(
  S.decodeTo(S.optional(S.toType(SharingInfo)), {
    decode: SchemaGetter.transformOptional(
      (value: Option.Option<S.Schema.Type<typeof SharingInfo> | null | undefined>) =>
        value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
),
  /**
* User-defined tags for this conversion
*/
"tags": S.Array(AudioConversionTag),
  /**
* The title of the document
*/
"title": S.NullOr(S.String),
  /**
* Permanent URL for publicly viewing this document
*/
"url_slug": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "AudioConversionSummary" });
export type AudioConversionSummary = typeof AudioConversionSummary.Type;

/**
* Response schema for GET requests on /v2/audio.
*/
export class GetListAudioV2200 extends S.Struct({
  /**
* Paginated list of user conversions
*/
"audio_conversions": S.Array(AudioConversionSummary),
  /**
* The cursor for fetching the next page of results (if exists)
*/
"cursor": S.NullOr(S.String),
  /**
* The version of the TTS finalized data on this backend code
*/
"tts_finalized_version": S.Int,
  /**
* The version of the voice data on this backend code
*/
"voice_data_version": S.Int
}) {}

/**
* Request schema for POST requests on /v2/audio/conversion.
*/
export const PostCreateAudioV2Request = S.Struct({
  /**
* The URL of a document (e.g. a PDF) to convert to audio
*/
"document_url": S.String,
  /**
* Optional title for the audio. If not provided, will be extracted from document.
*/
"title": S.optional(S.String),
  /**
* Optional ID of the folder to add this conversion to
*/
"folder_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "PostCreateAudioV2Request" });
export type PostCreateAudioV2Request = typeof PostCreateAudioV2Request.Type;

/**
* Response schema for POST requests on /v2/audio.
*/
export class PostCreateAudioV2200 extends S.Struct({
  /**
* The newly-created AudioConversion instance
*/
"audio_conversion": AudioConversionSummary
}) {}

export class PatchUpdateAudioV2RequestVisibilityEnum extends S.Literals(["private", "public", "selective_share"]) {}

/**
* Request schema for PATCH requests on /v2/audio/{id}.
*/
export const PatchUpdateAudioV2Request = S.Struct({
  /**
* List of email addresses to share with (only used when visibility is "selective_share")
*/
"shared_with_emails": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* List of share links (only used when visibility is "selective_share")
*/
"shared_with_links": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* List of user IDs to share with (only used when visibility is "selective_share")
*/
"shared_with_user_ids": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Overwrite user-defined tags for this conversion (set to empty list to delete all tags)
*/
"tags": S.NullOr(S.Array(AudioConversionTag)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* @default null
*/
"description": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Overwrite the title of the document. Ignored if empty or null.
*/
"title": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The sharing mode for this conversion
*/
"visibility": S.NullOr(PatchUpdateAudioV2RequestVisibilityEnum).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "PatchUpdateAudioV2Request" });
export type PatchUpdateAudioV2Request = typeof PatchUpdateAudioV2Request.Type;

/**
* Response schema for PATCH requests on /v2/audio/{id}.
*/
export class PatchUpdateAudioV2200 extends S.Struct({
  /**
* The updatedAudioConversion instance
*/
"audio_conversion": AudioConversionSummary
}) {}

/**
* Request body for POST requests on /v2/audio/{id}/finalize.
*/
export const PostFinalizeAudioV2Request = S.Struct({
  /**
* The voice that was used to generate the TTS data
*/
"voice": S.String
}).annotate({ identifier: "PostFinalizeAudioV2Request" });
export type PostFinalizeAudioV2Request = typeof PostFinalizeAudioV2Request.Type;

/**
* Response schema for GET requests on /v2/audio/{id}.
*/
export class PostFinalizeAudioV2200 extends S.Struct({
  /**
* The AudioConversion instance
*/
"audio_conversion": AudioConversionSummary
}) {}

/**
* Response schema for GET requests on /v2/audio/{id}.
*/
export class GetGetAudioV2200 extends S.Struct({
  /**
* The AudioConversion instance
*/
"audio_conversion": AudioConversionSummary
}) {}

export class GetListAudioChaptersV2Params extends S.Struct({
  "cursor": S.optional(S.NullOr(S.String)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.String)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  "limit": S.optional(S.NullOr(S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(1)), S.check(S.isLessThanOrEqualTo(4))))).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Int.pipe(S.check(S.isGreaterThanOrEqualTo(1)), S.check(S.isLessThanOrEqualTo(4))))), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (4 as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}) {}

/**
* Status of TTS audio generated for this chapter
*/
export class ChapterDetailAudioUrlStatus extends S.Literals(["pending", "scheduled", "inProgress", "completed", "failed"]) {}

/**
* Bounding box schema for a word, sentence, or paragraph within an AudioConversion.
*/
export const ChapterBbox = S.Struct({
  /**
* The page number for this bounding box (1-indexed)
*/
"page": S.Int
}).annotate({ identifier: "ChapterBbox" });
export type ChapterBbox = typeof ChapterBbox.Type;

/**
* Start and end timestamp for a word, sentence, paragraph, or chapter within an AudioConversion.
*/
export const ChapterTimestampMs = S.Struct({
  /**
* End time in milliseconds, counting from 0
*/
"end": S.Int,
  /**
* Start time in milliseconds, counting from 0
*/
"start": S.Int
}).annotate({ identifier: "ChapterTimestampMs" });
export type ChapterTimestampMs = typeof ChapterTimestampMs.Type;

export const ChapterWord = S.Struct({
  /**
* The bounding boxes of this word
*/
"bboxes": S.Array(ChapterBbox),
  /**
* The plain text of this word
*/
"text": S.String,
  /**
* The start and end times of this word
*/
"timestamp_ms": S.NullOr(ChapterTimestampMs),
  /**
* ID for this chunk of cached TTS, if this word begins a new chunk
*/
"tts_hash_id": S.NullOr(S.String)
}).annotate({ identifier: "ChapterWord" });
export type ChapterWord = typeof ChapterWord.Type;

export const ChapterSentence = S.Struct({
  /**
* The bounding boxes of this sentence
*/
"bboxes": S.Array(ChapterBbox),
  /**
* The start and end times of this sentence
*/
"timestamp_ms": S.NullOr(ChapterTimestampMs),
  /**
* List of words within this sentence
*/
"words": S.Array(ChapterWord)
}).annotate({ identifier: "ChapterSentence" });
export type ChapterSentence = typeof ChapterSentence.Type;

export const ChapterParagraph = S.Struct({
  /**
* The bounding boxes of this paragraph
*/
"bboxes": S.Array(ChapterBbox),
  /**
* List of sentences within this paragraph
*/
"sentences": S.Array(ChapterSentence),
  /**
* The start and end times of this paragraph
*/
"timestamp_ms": S.NullOr(ChapterTimestampMs)
}).annotate({ identifier: "ChapterParagraph" });
export type ChapterParagraph = typeof ChapterParagraph.Type;

/**
* Detail schema for a single chapter within an AudioConversion.
*/
export const ChapterDetail = S.Struct({
  /**
* The URL of the audio file for this chapter (if available)
*/
"audio_url": S.NullOr(S.String),
  /**
* Status of TTS audio generated for this chapter
*/
"audio_url_status": ChapterDetailAudioUrlStatus,
  /**
* Avg duration of each TTS chunk in milliseconds. Useful for rendering unified seek bar.Note: This value is hardcoded for now, the frontend should retain a cache of actual audio chunk durations. In the future this could become more accurate but will never be perfect because some chunks may have not been generated yet.
*/
"avg_estimated_tts_chunk_duration_ms": S.Int,
  /**
* Avg number of words in each paragraph. Useful for rendering reader mode.
*/
"avg_paragraph_word_length": S.Int,
  /**
* List of document page numbers that this chapter spans. Useful for rendering document mode.
*/
"bbox_pages": S.Array(S.Int),
  /**
* The ID of this chapter
*/
"id": S.String,
  /**
* Zero-based index of this chapter within the document
*/
"index": S.Int,
  /**
* Number of paragraphs in this chapter. Useful for rendering reader mode.
*/
"num_paragraphs": S.Int,
  /**
* List of paragraphs within this chapter
*/
"paragraphs": S.Array(ChapterParagraph),
  /**
* The start and end times of this chapter
*/
"timestamp_ms": S.NullOr(ChapterTimestampMs),
  /**
* The title of this chapter
*/
"title": S.String,
  /**
* Version of the TTS batch for this chapter
*/
"tts_batch_version": S.Int,
  /**
* Sorted list of text-to-speech chunk IDs in this chapter
*/
"tts_ids": S.Array(S.String)
}).annotate({ identifier: "ChapterDetail" });
export type ChapterDetail = typeof ChapterDetail.Type;

/**
* Response schema for GET requests on /v2/audio/{id}/chapters.
*/
export class GetListAudioChaptersV2200 extends S.Struct({
  /**
* Paginated list of chapters
*/
"chapters": S.Array(ChapterDetail),
  /**
* The cursor for fetching the next page of results (if exists)
*/
"cursor": S.NullOr(S.String)
}) {}

/**
* Status of TTS audio generated for this chapter
*/
export class ChapterSummaryAudioUrlStatus extends S.Literals(["pending", "scheduled", "inProgress", "completed", "failed"]) {}

/**
* Summary schema for a single chapter within an AudioConversion.
*/
export const ChapterSummary = S.Struct({
  /**
* The URL of the audio file for this chapter (if available)
*/
"audio_url": S.NullOr(S.String),
  /**
* Status of TTS audio generated for this chapter
*/
"audio_url_status": ChapterSummaryAudioUrlStatus,
  /**
* Avg duration of each TTS chunk in milliseconds. Useful for rendering unified seek bar.Note: This value is hardcoded for now, the frontend should retain a cache of actual audio chunk durations. In the future this could become more accurate but will never be perfect because some chunks may have not been generated yet.
*/
"avg_estimated_tts_chunk_duration_ms": S.Int,
  /**
* Avg number of words in each paragraph. Useful for rendering reader mode.
*/
"avg_paragraph_word_length": S.Int,
  /**
* List of document page numbers that this chapter spans. Useful for rendering document mode.
*/
"bbox_pages": S.Array(S.Int),
  /**
* The ID of this chapter
*/
"id": S.String,
  /**
* Zero-based index of this chapter within the document
*/
"index": S.Int,
  /**
* Number of paragraphs in this chapter. Useful for rendering reader mode.
*/
"num_paragraphs": S.Int,
  /**
* The title of this chapter
*/
"title": S.String,
  /**
* Version of the TTS batch for this chapter
*/
"tts_batch_version": S.Int,
  /**
* Sorted list of text-to-speech chunk IDs in this chapter
*/
"tts_ids": S.Array(S.String)
}).annotate({ identifier: "ChapterSummary" });
export type ChapterSummary = typeof ChapterSummary.Type;

/**
* The current status of the conversion
*/
export class AudioConversionDetailConversionStatus extends S.Literals(["initializing", "processingPDF", "completedExtraction", "complete", "deleted", "Error"]) {}

export class AudioConversionDetailDocumentTypeEnum extends S.Literals(["HTML", "PDF", "DOC", "DOCX", "PPT", "PPTX", "TXT", "MD", "RTF", "EPUB", "MOBI", "EMAIL", "EMAIL_ATTACHMENT"]) {}

export class AudioConversionDetailLanguageEnum extends S.Literals(["en", "es", "fr", "hi", "it", "ja", "pt", "zh"]) {}

/**
* A detailed representation of a UserPodcastItem and its corresponding AudioConversion.
*/
export const AudioConversionDetail = S.Struct({
  /**
* Unix timestamp (in milliseconds) when this conversion was archived
*/
"archived_at_ms": S.NullOr(S.Int).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Chapter summaries, if available
*/
"chapters": S.NullOr(S.Array(ChapterSummary)),
  /**
* The current status of the conversion
*/
"conversion_status": AudioConversionDetailConversionStatus,
  /**
* The URL of the cover image for this conversion
*/
"cover_img_url": S.NullOr(S.String),
  /**
* Unix timestamp (in milliseconds) when this user added the conversion
*/
"created_at_ms": S.Int,
  /**
* The type of document
*/
"document_type": S.NullOr(AudioConversionDetailDocumentTypeEnum).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The URL of the source document
*/
"document_url": S.NullOr(S.String),
  /**
* For failed conversions, an optional user-facing error message
*/
"error": S.NullOr(S.String),
  /**
* Metadata about the finalized conversion (if exists)
*/
"finalized_metadata": S.NullOr(AudioConversionFinalizedMetadata).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The ID of the folder containing this conversion (if exists)
*/
"folder_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Absolute path of the parent folder (list is empty if root)
*/
"folder_path": S.Array(S.String),
  /**
* Mongo-style ObjectId of this AudioConversion instance
*/
"id": S.String,
  /**
* Detected language of the document
*/
"language": S.NullOr(AudioConversionDetailLanguageEnum),
  /**
* Mongo-style ObjectId of the user who owns this conversion
*/
"owner_id": S.String,
  /**
* The number of pages in the document
*/
"page_count": S.NullOr(S.Int),
  /**
* Latest position, or null for pending conversion
*/
"progress": S.NullOr(AudioConversionProgress),
  /**
* Information about how this conversion is shared
*/
"sharing": S.optional(S.NullOr(SharingInfo)).pipe(
  S.decodeTo(S.optional(S.toType(SharingInfo)), {
    decode: SchemaGetter.transformOptional(
      (value: Option.Option<S.Schema.Type<typeof SharingInfo> | null | undefined>) =>
        value.pipe(Option.filter(Predicate.isNotNull)),
    ),
    encode: SchemaGetter.passthrough(),
  }),
),
  /**
* User-defined tags for this conversion
*/
"tags": S.Array(AudioConversionTag),
  /**
* The title of the document
*/
"title": S.NullOr(S.String),
  /**
* Permanent URL for publicly viewing this document
*/
"url_slug": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The voice used to generate the audio
*/
"voice": S.String
}).annotate({ identifier: "AudioConversionDetail" });
export type AudioConversionDetail = typeof AudioConversionDetail.Type;

/**
* Response schema for GET requests on /v2/audio/{id}/detailed.
*/
export class GetGetAudioDetailedV2200 extends S.Struct({
  /**
* The AudioConversion instance
*/
"audio_conversion": AudioConversionDetail
}) {}

/**
* Response schema for the /v2/chapter/<chapter_id> endpoint.
*/
export class GetGetChapterV2200 extends S.Struct({
  /**
* Chapter details
*/
"chapter": ChapterDetail
}) {}

export const PutUpdateChapterProgressV2Request = S.Struct({
  /**
* The user’s current audio position within this chapter, in milliseconds
*/
"timestamp_ms": S.Int,
  /**
* The ID of the TTS chunk that the user is currently listening to
*/
"tts_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "PutUpdateChapterProgressV2Request" });
export type PutUpdateChapterProgressV2Request = typeof PutUpdateChapterProgressV2Request.Type;

export class GetGetChapterTtsV2Params extends S.Struct({
  "voice": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}) {}

/**
* Start and end timestamp for a word in a TTS chunk.
*/
export const TtsTimestampMs = S.Struct({
  /**
* End time in milliseconds, counting from 0
*/
"end": S.Int,
  /**
* Start time in milliseconds, counting from 0
*/
"start": S.Int
}).annotate({ identifier: "TtsTimestampMs" });
export type TtsTimestampMs = typeof TtsTimestampMs.Type;

/**
* Text-to-speech data for a given chunk of text.
*/
export const TtsDetail = S.Struct({
  /**
* Start and end timestamps for each word in the text, in milliseconds
*/
"alignment_timestamps": S.Array(S.Union([TtsTimestampMs,
S.Record(S.String, S.Unknown)])),
  /**
* The duration of the output audio in milliseconds
*/
"duration_ms": S.Int,
  /**
* The format of the output audio file
*/
"format": S.Literal("mp3"),
  /**
* Unique ID representing the text-to-speech data for a given chunk of text
*/
"id": S.String,
  /**
* The text that corresponds to this TTS audio
*/
"text": S.String,
  /**
* The download URL of the output audio file
*/
"url": S.String,
  /**
* Timestamp for when the download URL expires (in milliseconds)
*/
"url_expires_at_ms": S.Int,
  /**
* The voice that was used to generate the TTS data
*/
"voice": S.String,
  /**
* The version of the TTS cache that generated this TTS data
*/
"voice_data_version": S.Int
}).annotate({ identifier: "TtsDetail" });
export type TtsDetail = typeof TtsDetail.Type;

/**
* Response schema for GET /v2/chapters/<chapter_id>/tts/<tts_id>.
*/
export class GetGetChapterTtsV2200 extends S.Struct({
  /**
* The text-to-speech data for the given tts chunk
*/
"tts": TtsDetail
}) {}

/**
* Summary schema for a single folder.
*/
export const FolderSummary = S.Struct({
  /**
* The permanent ID of this folder
*/
"id": S.String,
  /**
* Legacy MongoDB ID for migration comparison
*/
"legacy_id": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* Count of all audio files in this folder (recursive)
*/
"num_items": S.Int,
  /**
* The absolute path of this folder
*/
"path": S.Array(S.String),
  /**
* Thumbnails of up to 4 documents in this folder (recursive)
*/
"thumbnail_urls": S.Array(S.String)
}).annotate({ identifier: "FolderSummary" });
export type FolderSummary = typeof FolderSummary.Type;

/**
* Response schema for the GET /v2/folders endpoint.
* Note that users are limited to a finite number of folders so this endpoint is not paginated.
*/
export class GetListFolders200 extends S.Struct({
  "folders": S.Array(FolderSummary)
}) {}

/**
* Request schema for the POST /v2/folders endpoint.
*/
export const PostCreateFolderRequest = S.Struct({
  /**
* If set, move these conversions to this folder
*/
"audio_conversion_ids": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* The absolute path of the folder to create
*/
"path": S.Array(S.String)
}).annotate({ identifier: "PostCreateFolderRequest" });
export type PostCreateFolderRequest = typeof PostCreateFolderRequest.Type;

/**
* Detail schema for a single folder.
*/
export const FolderDetail = S.Struct({
  /**
* IDs of all conversions in this folder (not recursive)
*/
"audio_conversion_ids": S.Array(S.String),
  /**
* IDs of all child folders (not recursive)
*/
"child_folder_ids": S.Array(S.String),
  /**
* The permanent ID of this folder
*/
"id": S.String,
  /**
* Count of all audio files in this folder (recursive)
*/
"num_items": S.Int,
  /**
* The absolute path of this folder
*/
"path": S.Array(S.String),
  /**
* Thumbnails of up to 4 documents in this folder (recursive)
*/
"thumbnail_urls": S.Array(S.String)
}).annotate({ identifier: "FolderDetail" });
export type FolderDetail = typeof FolderDetail.Type;

/**
* Response schema for the POST /v2/folders endpoint.
*/
export class PostCreateFolder200 extends S.Struct({
  "folder": FolderDetail
}) {}

/**
* Response schema for the GET /v2/folders/<id> endpoint.
*/
export class GetGetFolder200 extends S.Struct({
  /**
* Folder details
*/
"folder": FolderDetail
}) {}

export class DeleteDeleteFolderParams extends S.Struct({
  "recursive": S.optional(S.NullOr(S.Boolean)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Boolean)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (false as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}) {}

/**
* Response schema for the DELETE /v2/folders/<id> endpoint.
*/
export class DeleteDeleteFolder200 extends S.Struct({
  /**
* IDs of all deleted conversions (if recursive delete)
*/
"deleted_conversion_ids": S.Array(S.String),
  /**
* IDs of all deleted folders
*/
"deleted_folder_ids": S.Array(S.String)
}) {}

/**
* Request schema for the PATCH /v2/folders/<id> endpoint.
*/
export const PatchUpdateFolderRequest = S.Struct({
  /**
* If set, move these conversions to this folder
*/
"audio_conversion_ids": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
),
  /**
* If set, moves this folder and all its contents to the new absolute path
*/
"path": S.NullOr(S.Array(S.String)).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}).annotate({ identifier: "PatchUpdateFolderRequest" });
export type PatchUpdateFolderRequest = typeof PatchUpdateFolderRequest.Type;

/**
* Response schema for the PATCH /v2/folders/<id> endpoint.
*/
export class PatchUpdateFolder200 extends S.Struct({
  "folder": FolderDetail
}) {}

/**
* Request schema for POST /v2/tts/batch-text.
*/
export const PostCreateTtsBatchesFromTextRequest = S.Struct({
  /**
* The text that should be pre-processed and batched for text-to-speech.
*/
"text": S.String
}).annotate({ identifier: "PostCreateTtsBatchesFromTextRequest" });
export type PostCreateTtsBatchesFromTextRequest = typeof PostCreateTtsBatchesFromTextRequest.Type;

/**
* Represents a streamable chunk of text-to-speech content.
*/
export const CreateTtsBatchTextResponseItem = S.Struct({
  /**
* A streamable chunk of text that corresponds to the given tts_id.
*/
"text": S.String,
  /**
* Unique ID representing a chunk of streamable text-to-speech audio
*/
"tts_id": S.String
}).annotate({ identifier: "CreateTtsBatchTextResponseItem" });
export type CreateTtsBatchTextResponseItem = typeof CreateTtsBatchTextResponseItem.Type;

/**
* Response schema for POST /v2/tts/batch-text.
*/
export class PostCreateTtsBatchesFromText200 extends S.Struct({
  /**
* A list of streamable text-to-speech batches.
*/
"batches": S.Array(CreateTtsBatchTextResponseItem)
}) {}

/**
* Request schema for POST /v2/tts/demo-text.
*/
export const PostDemoTtsBatchesFromTextRequest = S.Struct({
  /**
* The text that should be pre-processed and batched for text-to-speech.
*/
"text": S.String,
  /**
* Either a voice name or voice model ID
*/
"voice": S.optional(S.NullOr(S.String)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.String)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("Will" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}).annotate({ identifier: "PostDemoTtsBatchesFromTextRequest" });
export type PostDemoTtsBatchesFromTextRequest = typeof PostDemoTtsBatchesFromTextRequest.Type;

/**
* Response schema for POST /v2/tts.
*/
export class PostDemoTtsBatchesFromText200 extends S.Struct({
  /**
* The text-to-speech data for the given input text
*/
"tts": TtsDetail
}) {}

export class GetGetTtsParams extends S.Struct({
  "voice": S.NullOr(S.String).pipe(
  S.withDecodingDefaultType(Effect.sync(() => null)),
)
}) {}

/**
* Response schema for POST /v2/tts.
*/
export class GetGetTts200 extends S.Struct({
  /**
* The text-to-speech data for the given input text
*/
"tts": TtsDetail
}) {}

export class VoiceSummaryGender extends S.Literals(["male", "female"]) {}

/**
* The language of this voice
*/
export class VoiceSummaryLang extends S.Literals(["en", "es", "fr", "hi", "it", "ja", "pt", "zh"]) {}

/**
* The model ID of this voice
*/
export class VoiceSummaryModelId extends S.Literals(["af_alloy", "af_aoede", "af_bella", "af_heart", "af_jadzia", "af_jessica", "af_kore", "af_nicole", "af_nova", "af_river", "af_sarah", "af_sky", "af_v0", "af_v0bella", "af_v0irulan", "af_v0nicole", "af_v0sarah", "af_v0sky", "am_adam", "am_echo", "am_eric", "am_fenrir", "am_liam", "am_michael", "am_onyx", "am_puck", "am_santa", "am_v0adam", "am_v0gurney", "am_v0michael", "bf_alice", "bf_emma", "bf_lily", "bf_v0emma", "bf_v0isabella", "bm_daniel", "bm_fable", "bm_george", "bm_lewis", "bm_v0george", "bm_v0lewis", "ef_dora", "em_alex", "em_santa", "ff_siwis", "hf_alpha", "hf_beta", "hm_omega", "hm_psi", "if_sara", "im_nicola", "jf_alpha", "jf_gongitsune", "jf_nezumi", "jf_tebukuro", "jm_kumo", "pf_dora", "pm_alex", "pm_santa", "zf_xiaobei", "zf_xiaoni", "zf_xiaoxiao", "zf_xiaoyi", "zm_yunjian", "zm_yunxi", "zm_yunxia", "zm_yunyang"]) {}

/**
* The softness of this voice
*/
export class VoiceSummarySoftness extends S.Literals(["soft", "moderate", "deep"]) {}

/**
* d
* Summary schema for a single voice.
*/
export const VoiceSummary = S.Struct({
  "gender": VoiceSummaryGender,
  /**
* Images were generated with midjourney.
* Prompt examples:
* Realistic professional avatar of a [age, gender, ethnicity], face-to-shoulders view, lifelike skin texture,
* natural expression, clear eyes, soft lighting, subtle background blur, ultra-HD quality.
* 
* Realistic, polished avatar of a person, with a light gray background inside a circular frame, soft lighting,
* minimalistic design, and a friendly expression.
*/
"image_url": S.String,
  /**
* Whether this voice is free
*/
"is_free": S.optional(S.NullOr(S.Boolean)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(S.Boolean)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => (false as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
),
  /**
* The language of this voice
*/
"lang": VoiceSummaryLang,
  /**
* The model ID of this voice
*/
"model_id": VoiceSummaryModelId,
  /**
* The name of this voice
*/
"name": S.String,
  "sample_url": S.String,
  /**
* The softness of this voice
*/
"softness": S.optional(S.NullOr(VoiceSummarySoftness)).pipe(
  S.decodeTo(S.UndefinedOr(S.toType(VoiceSummarySoftness)), {
    decode: SchemaGetter.transformOptional((value) =>
      value.pipe(
        Option.filter(Predicate.isNotNull),
        Option.orElseSome(() => ("moderate" as const)),
      ),
    ),
    encode: SchemaGetter.required(),
  }),
)
}).annotate({ identifier: "VoiceSummary" });
export type VoiceSummary = typeof VoiceSummary.Type;

/**
* Response schema for the GET /v2/voices endpoint.
*/
export class GetListVoices200 extends S.Struct({
  "voices": S.Array(VoiceSummary)
}) {}

export const make = (
  httpClient: HttpClient.HttpClient, 
  options: {
    readonly transformClient?: ((client: HttpClient.HttpClient) => Effect.Effect<HttpClient.HttpClient>) | undefined
  } = {}
): LegacyClient => {
  const unexpectedStatus = (response: HttpClientResponse.HttpClientResponse) =>
    Effect.flatMap(
      Effect.orElseSucceed(response.json, () => "Unexpected status code"),
      (description: unknown) =>
        Effect.fail(
          new HttpClientError.HttpClientError({
            reason: new HttpClientError.StatusCodeError({
              request: response.request,
              response,
              description: typeof description === "string" ? description : JSON.stringify(description),
            }),
          }),
        ),
    )
  const withResponse: <A, E>(
    f: (response: HttpClientResponse.HttpClientResponse) => Effect.Effect<A, E, never>,
  ) => (
    request: HttpClientRequest.HttpClientRequest,
  ) => Effect.Effect<any, any, never> = options.transformClient
    ? (f) => (request) =>
        Effect.flatMap(
          Effect.flatMap(options.transformClient!(httpClient), (client: HttpClient.HttpClient) =>
            client.execute(request),
          ),
          f,
        )
    : (f) => (request) => Effect.flatMap(httpClient.execute(request), f)
  const decodeSuccess =
    <T extends S.Constraint>(schema: T) =>
    (response: HttpClientResponse.HttpClientResponse) =>
      HttpClientResponse.schemaBodyJson(schema)(response)
  const decodeError =
    <const Tag extends string, T extends S.Constraint>(tag: Tag, schema: T) =>
    (response: HttpClientResponse.HttpClientResponse) =>
      Effect.flatMap(
        HttpClientResponse.schemaBodyJson(schema)(response),
        (cause: T["Type"]) => Effect.fail(LegacyClientError(tag, cause, response)),
      )
  return {
    httpClient,
    "getIndex": () => HttpClientRequest.get(`/`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getAppleAppSiteAssociation": () => HttpClientRequest.get(`/.well-known/apple-app-site-association`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getAppleAssetLinks": () => HttpClientRequest.get(`/.well-known/assetlinks.json`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getListAudio": () => HttpClientRequest.get(`/audio/`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postDeleteAudioItem": () => HttpClientRequest.post(`/audio/deleteAudioItem`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetProcessingQueue": () => HttpClientRequest.get(`/audio/getProcessingQueue`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetAudio": (audioId) => HttpClientRequest.get(`/audio/${audioId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "patchUpdateAudio": (audioId) => HttpClientRequest.patch(`/audio/${audioId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetAudioChapter": (audioId, chapterId) => HttpClientRequest.get(`/audio/${audioId}/chapter/${chapterId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getTestError": () => HttpClientRequest.get(`/errorcheck`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGet": () => HttpClientRequest.get(`/healthcheck`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetSlow": () => HttpClientRequest.get(`/healthcheck_slow`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetPaymentDetails": () => HttpClientRequest.get(`/money/getPaymentDetails`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postGetPaymentDetails": () => HttpClientRequest.post(`/money/getPaymentDetails`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getListUserNotes": () => HttpClientRequest.get(`/notes/`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postUpsertManyNotes": () => HttpClientRequest.post(`/notes/upsert-many`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetNoteById": (noteId) => HttpClientRequest.get(`/notes/${noteId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "deleteDeleteUserNote": (noteId) => HttpClientRequest.make("DELETE")(`/notes/${noteId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetSubscriptions": () => HttpClientRequest.get(`/payment/subscriptions`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetSubscriptions200),
      orElse: unexpectedStatus
    }))
  ),
  "postGetUploadUrl": () => HttpClientRequest.post(`/pdf/get-upload-url`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetPodcastEpisode": (guid) => HttpClientRequest.get(`/podcast/episode/${guid}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postCreatePodcastInteraction": () => HttpClientRequest.post(`/podcast/interaction`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postStripePurchaseUpsell": () => HttpClientRequest.post(`/stripe/purchase-upsell`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postStripeWebhook": () => HttpClientRequest.post(`/stripe/webhook`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getTestExceptionCheck": () => HttpClientRequest.get(`/test_exception_check`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getUptimeTest": () => HttpClientRequest.get(`/test_uptime`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getTimeoutErrorCheck": () => HttpClientRequest.get(`/timeout_error_check`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postTextToPhonemes": (options) => HttpClientRequest.post(`/tts/text-to-phonemes`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostTextToPhonemes200),
      orElse: unexpectedStatus
    }))
  ),
  "postB2BContactForm": () => HttpClientRequest.post(`/user/b2b-contact-form`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postDeleteUser": () => HttpClientRequest.post(`/user/deleteUser`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postFeedback": () => HttpClientRequest.post(`/user/feedback`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postForgotPassword": () => HttpClientRequest.post(`/user/forgotPassword`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postForgotPasswordVerifyCode": () => HttpClientRequest.post(`/user/forgotPasswordVerifyCode`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postLogin": (options) => HttpClientRequest.post(`/user/login`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostLogin200),
      orElse: unexpectedStatus
    }))
  ),
  "postOnboardingComplete": () => HttpClientRequest.post(`/user/onboardingComplete`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postPostCancelSurvey": () => HttpClientRequest.post(`/user/post-cancel-survey`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postPostPurchaseSurveySubmit": () => HttpClientRequest.post(`/user/post-purchase-survey`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetPreferences": () => HttpClientRequest.get(`/user/preferences`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postUpdatePreferences": () => HttpClientRequest.post(`/user/preferences`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetProfile": () => HttpClientRequest.get(`/user/profile`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "patchPatchProfile": () => HttpClientRequest.patch(`/user/profile`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetReferralList": () => HttpClientRequest.get(`/user/referral-list`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetReferralList200),
      orElse: unexpectedStatus
    }))
  ),
  "postRegister": (options) => HttpClientRequest.post(`/user/register`).pipe(
    HttpClientRequest.setUrlParams({ "random_password": options.params?.["random_password"] as any }),
    HttpClientRequest.bodyJsonUnsafe(options.payload),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostRegister200),
      "409": decodeError("PostRegister409", PostRegister409),
      orElse: unexpectedStatus
    }))
  ),
  "postSetFcmToken": (options) => HttpClientRequest.post(`/user/set-fcm-token`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "postSetReferredByCode": (options) => HttpClientRequest.post(`/user/set-referred-by`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getSummary": () => HttpClientRequest.get(`/user/summary`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetSummary200),
      orElse: unexpectedStatus
    }))
  ),
  "postUpdatePassword": () => HttpClientRequest.post(`/user/update-password`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getListAudioV2": (options) => HttpClientRequest.get(`/v2/audio`).pipe(
    HttpClientRequest.setUrlParams({ "cursor": options?.["cursor"] as any, "sort_by": options?.["sort_by"] as any, "status": options?.["status"] as any, "except_status": options?.["except_status"] as any, "folder_id": options?.["folder_id"] as any, "if_modified_since_ms": options?.["if_modified_since_ms"] as any, "limit": options?.["limit"] as any }),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetListAudioV2200),
      orElse: unexpectedStatus
    }))
  ),
  "postCreateAudioV2": (options) => HttpClientRequest.post(`/v2/audio`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostCreateAudioV2200),
      orElse: unexpectedStatus
    }))
  ),
  "deleteDeleteAudioV2": (audioId) => HttpClientRequest.make("DELETE")(`/v2/audio/${audioId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "patchUpdateAudioV2": (audioId, options) => HttpClientRequest.patch(`/v2/audio/${audioId}`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PatchUpdateAudioV2200),
      orElse: unexpectedStatus
    }))
  ),
  "postFinalizeAudioV2": (audioId, options) => HttpClientRequest.post(`/v2/audio/${audioId}/finalize`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostFinalizeAudioV2200),
      orElse: unexpectedStatus
    }))
  ),
  "getGetAudioV2": (audioOrSlug) => HttpClientRequest.get(`/v2/audio/${audioOrSlug}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetAudioV2200),
      orElse: unexpectedStatus
    }))
  ),
  "getListAudioChaptersV2": (audioOrSlug, options) => HttpClientRequest.get(`/v2/audio/${audioOrSlug}/chapters`).pipe(
    HttpClientRequest.setUrlParams({ "cursor": options?.["cursor"] as any, "limit": options?.["limit"] as any }),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetListAudioChaptersV2200),
      orElse: unexpectedStatus
    }))
  ),
  "getGetAudioDetailedV2": (audioOrSlug) => HttpClientRequest.get(`/v2/audio/${audioOrSlug}/detailed`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetAudioDetailedV2200),
      orElse: unexpectedStatus
    }))
  ),
  "getGetChapterV2": (chapterId) => HttpClientRequest.get(`/v2/chapter/${chapterId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetChapterV2200),
      orElse: unexpectedStatus
    }))
  ),
  "putUpdateChapterProgressV2": (chapterId, options) => HttpClientRequest.put(`/v2/chapter/${chapterId}/progress`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getGetChapterTtsV2": (chapterId, ttsId, options) => HttpClientRequest.get(`/v2/chapter/${chapterId}/tts/${ttsId}`).pipe(
    HttpClientRequest.setUrlParams({ "voice": options?.["voice"] as any }),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetChapterTtsV2200),
      orElse: unexpectedStatus
    }))
  ),
  "postMailgunWebhookV2": () => HttpClientRequest.post(`/v2/email/mailgun-webhook`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  ),
  "getListFolders": () => HttpClientRequest.get(`/v2/folders`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetListFolders200),
      orElse: unexpectedStatus
    }))
  ),
  "postCreateFolder": (options) => HttpClientRequest.post(`/v2/folders`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostCreateFolder200),
      orElse: unexpectedStatus
    }))
  ),
  "getGetFolder": (folderId) => HttpClientRequest.get(`/v2/folders/${folderId}`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetFolder200),
      orElse: unexpectedStatus
    }))
  ),
  "deleteDeleteFolder": (folderId, options) => HttpClientRequest.make("DELETE")(`/v2/folders/${folderId}`).pipe(
    HttpClientRequest.setUrlParams({ "recursive": options?.["recursive"] as any }),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(DeleteDeleteFolder200),
      orElse: unexpectedStatus
    }))
  ),
  "patchUpdateFolder": (folderId, options) => HttpClientRequest.patch(`/v2/folders/${folderId}`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PatchUpdateFolder200),
      orElse: unexpectedStatus
    }))
  ),
  "postCreateTtsBatchesFromText": (options) => HttpClientRequest.post(`/v2/tts/batch-text`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostCreateTtsBatchesFromText200),
      orElse: unexpectedStatus
    }))
  ),
  "postDemoTtsBatchesFromText": (options) => HttpClientRequest.post(`/v2/tts/demo-text`).pipe(
    HttpClientRequest.bodyJsonUnsafe(options),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(PostDemoTtsBatchesFromText200),
      orElse: unexpectedStatus
    }))
  ),
  "getGetTts": (ttsId, options) => HttpClientRequest.get(`/v2/tts/${ttsId}`).pipe(
    HttpClientRequest.setUrlParams({ "voice": options?.["voice"] as any }),
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetGetTts200),
      orElse: unexpectedStatus
    }))
  ),
  "getListVoices": () => HttpClientRequest.get(`/v2/voices`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      "2xx": decodeSuccess(GetListVoices200),
      orElse: unexpectedStatus
    }))
  ),
  "postRevenueCatWebhook": () => HttpClientRequest.post(`/webhooks/revenutCat`).pipe(
    withResponse(HttpClientResponse.matchStatus({
      orElse: unexpectedStatus
    }))
  )
  }
}

export interface LegacyClient {
  readonly httpClient: HttpClient.HttpClient
  readonly "getIndex": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getAppleAppSiteAssociation": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getAppleAssetLinks": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* The list is ordered by their creation time descending.
*/
readonly "getListAudio": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "postDeleteAudioItem": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getGetProcessingQueue": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Return the AudioConversion object for the given ID.
*/
readonly "getGetAudio": (audioId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Update a field on the User model.
*/
readonly "patchUpdateAudio": (audioId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Return the `ChapterDetail` instance with the given ID.
*/
readonly "getGetAudioChapter": (audioId: string, chapterId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getTestError": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getGet": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getGetSlow": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getGetPaymentDetails": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "postGetPaymentDetails": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Optional query params:
* - `audio_conversion_id`: Only return notes for this audioConversionId
* - `cursor_id`: Only return notes that were updated after this note
* - `limit`: Only return this many notes. Max limit of 50
* - `include_deleted`: If true, include deleted notes in the response
* 
* Returns a list of notes and a cursor that can be used to fetch the next page of notes.
* 
* Example:
*     curl -X GET -L '127.0.0.1:8000/notes/?limit=20'                 -H "Authorization: {yourAuthToken}"
*/
readonly "getListUserNotes": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Request body should be a JSON object with a single field, `notes`, which is a list of notes.
* 
* The frontend should only include an id if the note already exists in the DB. If the note does not exist in the
* DB, the frontend should omit the id field. The backend will generate an id for the note.
* 
* Example:
*     curl -X POST -L '127.0.0.1:8000/notes/upsert_many'                 -H "Authorization: {yourAuthToken}"                 -H "Content-Type: application/json"                 -d '{
*             "notes": [
*                 {
*                     "id": "65544aae14bdd7e082beb8eb", # optional, omit if note does not exist in DB
*                     "title": "title1",
*                     "body": "body1",
*                     "audio_conversion_id": "65548026260ecfa44b4989a9",
*                     "user_id": "655480195bec9c8082950d29"
*                 },
*             ]
*         }'
*/
readonly "postUpsertManyNotes": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
* curl -X GET -L '127.0.0.1:8000/notes/65544aae14bdd7e082beb8eb'             -H "Authorization: {yourAuthToken}"
*/
readonly "getGetNoteById": (noteId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*     curl -X DELETE -L '127.0.0.1:8000/notes/65544aae14bdd7e082beb8eb'                 -H "Authorization: {yourAuthToken}"
*/
readonly "deleteDeleteUserNote": (noteId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Note that a user can have both subscriptions present, one, or none.
*/
readonly "getGetSubscriptions": () => Effect.Effect<S.Schema.Type<typeof GetGetSubscriptions200>, HttpClientError.HttpClientError | ParseError>
  /**
* This endpoint originally only supported PDFs, but now supports all convertible file types.
* 
* Return a direct upload link for a convertible file.
*/
readonly "postGetUploadUrl": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* The response schema is **not** yet validated via *quart-schema* because
* the data is dynamically retrieved from Supabase and the exact field
* list may evolve; the current unit-tests only assert a subset of keys.
*/
readonly "getGetPodcastEpisode": (guid: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Expected JSON body::
* 
*     {
*         "episode_id": 123,        // optional (nullable)
*         "podcast_id": 456,        // optional (nullable)
*         "collection_id": "789",   // optional (nullable) - will be resolved to podcast_id
*         "type": "subscribe",      // required
*         "details": {"foo": "bar"} // optional dictionary with additional info
*     }
* 
* At least one of episode_id, podcast_id, or collection_id must be provided.
* If collection_id is provided, it will be resolved to a podcast_id by querying
* the podcasts table for the matching itunes_collection_id.
*/
readonly "postCreatePodcastInteraction": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* The upsell details must be passed in a JWT so that...
*  1. The client can't tamper with the upsell details
*  2. We don't have to predefine the upsell details on the backend
* 
* The contents of the JWT must be: {'amount_cents': int, 'period': "month" | "year", 'num_free_periods': int}
* Create new upsell JWTs with the script, `listening/scripts/create_upsell_jwt.py`.
* 
* Example:
*     curl -X POST                 -d '{"upsell_jwt": "encrypted JWT payload goes here..."}'                 -H "Authorization: {yourAuthToken}"                 -H "Content-Type: application/json"                 localhost:8000/stripe/purchase-upsell
*/
readonly "postStripePurchaseUpsell": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* https://stripe.com/docs/billing/subscriptions/webhooks
*/
readonly "postStripeWebhook": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getTestExceptionCheck": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getUptimeTest": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "getTimeoutErrorCheck": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*   curl -X POST 127.0.0.1:8000/tts/text-to-phonemes             -d '{"text": "Hello, world!", "lang": "en"}'             -H 'Content-Type: application/json'
*/
readonly "postTextToPhonemes": (options: S.Codec.Encoded<typeof PostTextToPhonemesRequest>) => Effect.Effect<S.Schema.Type<typeof PostTextToPhonemes200>, HttpClientError.HttpClientError | ParseError>
  /**
* Receive data from our B2B contact form and forward it to airtable.
*/
readonly "postB2BContactForm": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "postDeleteUser": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Expects a JSON body with one of three formats.
* 
* Legacy string: {"feedback": "I love listening.io!"}
* 
* Legacy JSON: {"feedback": "<raw_json>"}
* where <raw_json> is stringified JSON with the following fields:
*     user_message: str
*     report_type: Optional[str] = None
*     audio_conversion_id: Optional[str] = None
*     paragraph_index: Optional[int] = None
*     audio_url: Optional[str] = None
*     chapter_id: Optional[str] = None
*     raw_text: Optional[str] = None
*     timecode: Optional[int] = None
* 
* Just JSON: {"feedback": {
*     "user_message": "I love listening.io!",
*     ...other fields...
*     }
* }
* 
* Ideally, we can migrate all feedback requests to just be JSON.
*/
readonly "postFeedback": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Blocks users who are throttled for too many recent reset attempts.
* 
* This route uses the UserPasswordReset model,
* which is designed as a state machine for throttling reset attempts.
* Importantly, its methods transition its internal state without persisting
* anything to the database.
* Be sure to call reset.save_all() before returning if anything should be persisted.
*/
readonly "postForgotPassword": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* This route uses the UserPasswordReset model,
* which is designed as a state machine for throttling reset attempts.
* Importantly, its methods transition its internal state without persisting
* anything to the database.
* Be sure to call reset.save_all() before returning if anything should be persisted.
*/
readonly "postForgotPasswordVerifyCode": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  readonly "postLogin": (options: S.Codec.Encoded<typeof PostLoginRequest>) => Effect.Effect<S.Schema.Type<typeof PostLogin200>, HttpClientError.HttpClientError | ParseError>
  readonly "postOnboardingComplete": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Receive data from our post-cancel survey form and forward it to airtable.
*/
readonly "postPostCancelSurvey": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*     curl -X POST                 -d '{"data": {"acquisition_source": "podcast", "details": "Tim Ferriss"}}'                 -H "Content-Type: application/json"                 -H "Authorization: {yourAuthToken}"                 127.0.0.1:8000/user/post-purchase-survey
*/
readonly "postPostPurchaseSurveySubmit": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Returns the UserPreferences model for the current user as JSON.
*/
readonly "getGetPreferences": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* As-of October 2023, "voice" is the only preference we support.
* 
* Example:
*     curl -X POST                 -d '{"updates": {"voice": "AMY"}}'                 -H "Content-Type: application/json"                 -H "Authorization: {yourAuthToken}"                 127.0.0.1:8000/user/preferences
*/
readonly "postUpdatePreferences": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Returns the UserProfile model for the current user as JSON.
*/
readonly "getGetProfile": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* The endpoint merges the provided updates into the existing profile.
* 
* Example:
*     curl -X PATCH                 -d '{"updates": {"first_name": "John", "last_name": "Doe"}}'                 -H "Content-Type: application/json"                 -H "Authorization: {yourAuthToken}"                 127.0.0.1:8000/user/profile
*/
readonly "patchPatchProfile": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* The list is capped to 3.
* 
* This endpoint is used to show a user how many referrals they still need to earn a free subscription.
*/
readonly "getGetReferralList": () => Effect.Effect<S.Schema.Type<typeof GetGetReferralList200>, HttpClientError.HttpClientError | ParseError>
  /**
* If `price_id` and `trial_period_days` are provided, we create a new Stripe customer and subscription.
* This will also attach a `client_secret` to the response, which the client can use with Stripe Elements
* to sign up for the subscription.
* 
* `promo_code` is optional and will be saved to the user's record if provided.
* 
* The `random_password` query param is optional and will generate a random password for the user.
* If enabled, the response will include the random password.
* 
* This endpoint is used by the mobile app and marketing landing page.
*/
readonly "postRegister": (options: { readonly params?: S.Codec.Encoded<typeof PostRegisterParams> | undefined; readonly payload: S.Codec.Encoded<typeof PostRegisterRequest> }) => Effect.Effect<S.Schema.Type<typeof PostRegister200>, HttpClientError.HttpClientError | ParseError | LegacyClientError<"PostRegister409", S.Schema.Type<typeof PostRegister409>>>
  /**
* Set the FCM token for the user.
*/
readonly "postSetFcmToken": (options: S.Codec.Encoded<typeof PostSetFcmTokenRequest>) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* This endpoint also rewards the referrer if the conditions are met.
*/
readonly "postSetReferredByCode": (options: S.Codec.Encoded<typeof PostSetReferredByCodeRequest>) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* This should be succinct and fast to return. Avoid adding new fields unless they're absolutely necessary.
* 
* Example:
*     curl 127.0.0.1:8000/user/summary -H "Authorization: {yourAuthToken}"
*/
readonly "getSummary": () => Effect.Effect<S.Schema.Type<typeof GetSummary200>, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*     curl -X POST                 -d '{"data": {"old_password": "abc123", "new_password": "xyz789"}}'                 -H "Content-Type: application/json"                 -H "Authorization: {yourAuthToken}"                 127.0.0.1:8000/user/update-password
*/
readonly "postUpdatePassword": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Note that deleted conversions are *always* excluded, unless explicitly requested
* OR if using `If-Modified-Since` (so the client can detect deletions from another device).
* 
* Examples:
*     curl localhost:8000/v2/audio?limit=20&cursor=abc123&not_status=completed                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"                 -H "If-Modified-Since: Wed, 21 Oct 2015 07:28:00 GMT" (optional)
*/
readonly "getListAudioV2": (options?: S.Codec.Encoded<typeof GetListAudioV2Params> | undefined) => Effect.Effect<S.Schema.Type<typeof GetListAudioV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*     curl -X POST localhost:8000/v2/audio                 -d '{"document_url": "https://css4.pub/2017/newsletter/drylab.pdf"}'                 -H "Authorization: {yourAuthToken}"                 -H "Content-Type: application/json"
*/
readonly "postCreateAudioV2": (options: S.Codec.Encoded<typeof PostCreateAudioV2Request>) => Effect.Effect<S.Schema.Type<typeof PostCreateAudioV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Returns a 204 response on success. Returns 404 if the audio item does not exist or is already deleted.
* 
* Examples:
*     curl -X DELETE localhost:8000/v2/audio/{id}                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "deleteDeleteAudioV2": (audioId: string) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* - Basic properties: title, tags
* - Sharing settings: visibility, shared_with_emails, shared_with_user_ids, shared_with_links
* 
* Note that this overwrites fields completely, so be extra careful updating lists and mappings.
* 
* Examples:
*     curl -X PATCH localhost:8000/v2/audio/{id}                 -d '{"tags": [{"label": "Favorites", "color": "#BADA55"}]}'                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
* 
*     curl -X PATCH localhost:8000/v2/audio/{id}                 -d '{"visibility": "selective_share", "shared_with_emails": ["user@example.com"]}'                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "patchUpdateAudioV2": (audioId: string, options: S.Codec.Encoded<typeof PatchUpdateAudioV2Request>) => Effect.Effect<S.Schema.Type<typeof PatchUpdateAudioV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Finalize the given audio conversion.
*/
readonly "postFinalizeAudioV2": (audioId: string, options: S.Codec.Encoded<typeof PostFinalizeAudioV2Request>) => Effect.Effect<S.Schema.Type<typeof PostFinalizeAudioV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Return the AudioConversion object for the given ID or share ID.
*/
readonly "getGetAudioV2": (audioOrSlug: string) => Effect.Effect<S.Schema.Type<typeof GetGetAudioV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Currently, chapters are always returned in order (first to last).
* 
* Examples:
*     curl localhost:8000/v2/audio/{id}/chapters?limit=10&cursor=abc123                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "getListAudioChaptersV2": (audioOrSlug: string, options?: S.Codec.Encoded<typeof GetListAudioChaptersV2Params> | undefined) => Effect.Effect<S.Schema.Type<typeof GetListAudioChaptersV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Only should request this when the user has opened this document.
*/
readonly "getGetAudioDetailedV2": (audioOrSlug: string) => Effect.Effect<S.Schema.Type<typeof GetGetAudioDetailedV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Return the Chapter object for the given ID.
*/
readonly "getGetChapterV2": (chapterId: string) => Effect.Effect<S.Schema.Type<typeof GetGetChapterV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Set the user's current audio position within this chapter (in milliseconds).
*/
readonly "putUpdateChapterProgressV2": (chapterId: string, options: S.Codec.Encoded<typeof PutUpdateChapterProgressV2Request>) => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Return the Text-to-Speech object for the given ID.
*/
readonly "getGetChapterTtsV2": (chapterId: string, ttsId: string, options?: S.Codec.Encoded<typeof GetGetChapterTtsV2Params> | undefined) => Effect.Effect<S.Schema.Type<typeof GetGetChapterTtsV2200>, HttpClientError.HttpClientError | ParseError>
  /**
* Emails sent to any `@mail.listening.io` email are forwarded to production. Emails sent to to our sandbox domain
* (`@sandboxae9811af481544efbbbd1e1554f7b9c2.mailgun.org`) are forwarded to staging.
* 
* If an email contains any attachments in a file format we support, each of those attachments will be converted.
* If an email contains no supported attachments, the subject line and body of the email are converted as text.
* 
* https://documentation.mailgun.com/docs/mailgun/user-manual/receive-forward-store/#storing-and-retrieving-messages
*/
readonly "postMailgunWebhookV2": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
  /**
* Users are allowed a finite number of folders so this endpoint is not paginated.
* 
* Example:
*     curl localhost:8000/v2/folders                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "getListFolders": () => Effect.Effect<S.Schema.Type<typeof GetListFolders200>, HttpClientError.HttpClientError | ParseError>
  readonly "postCreateFolder": (options: S.Codec.Encoded<typeof PostCreateFolderRequest>) => Effect.Effect<S.Schema.Type<typeof PostCreateFolder200>, HttpClientError.HttpClientError | ParseError>
  /**
* Example:
*     curl localhost:8000/v2/folders/root                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "getGetFolder": (folderId: string) => Effect.Effect<S.Schema.Type<typeof GetGetFolder200>, HttpClientError.HttpClientError | ParseError>
  /**
* If `?recursive=true` is set, the folder and all its contents (including subfolders) are permanently deleted.
* 
* Example:
*     curl -X DELETE localhost:8000/v2/folders/abc123                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "deleteDeleteFolder": (folderId: string, options?: S.Codec.Encoded<typeof DeleteDeleteFolderParams> | undefined) => Effect.Effect<S.Schema.Type<typeof DeleteDeleteFolder200>, HttpClientError.HttpClientError | ParseError>
  /**
* This endpoint can be used both to move/rename folders and to move audio conversions between folders.
* 
* Example:
*     curl -X PATCH localhost:8000/v2/folders/abc123                 -d '{"path": ["new", "folder", "path"], "audio_conversion_ids": ["def456"]}'                 -H "Authorization {yourAuthtoken}"                 -H "Content-Type: application/json"
*/
readonly "patchUpdateFolder": (folderId: string, options: S.Codec.Encoded<typeof PatchUpdateFolderRequest>) => Effect.Effect<S.Schema.Type<typeof PatchUpdateFolder200>, HttpClientError.HttpClientError | ParseError>
  /**
* Paragraphs in the input text should always be separated by newlines "\n".
* 
*         Example:
*           curl -X POST 127.0.0.1:8000/v2/tts/batch-text             -d '{"text": "Paragraph 1.
* Paragraph2!"}'             -H 'Content-Type: application/json'
*             -H 'Authorization: {jwt}'
*         -> {"batches": [
*             {"tts_id": "abc123", "text": "Paragraph 1."},
*             {"tts_id": "def456", "text": "Paragraph 2!"}
*           ]}
*/
readonly "postCreateTtsBatchesFromText": (options: S.Codec.Encoded<typeof PostCreateTtsBatchesFromTextRequest>) => Effect.Effect<S.Schema.Type<typeof PostCreateTtsBatchesFromText200>, HttpClientError.HttpClientError | ParseError>
  /**
* Demo tts for our homepage.
*/
readonly "postDemoTtsBatchesFromText": (options: S.Codec.Encoded<typeof PostDemoTtsBatchesFromTextRequest>) => Effect.Effect<S.Schema.Type<typeof PostDemoTtsBatchesFromText200>, HttpClientError.HttpClientError | ParseError>
  /**
* Do *not* use this endpoint to stream chapter audio, instead use `/v2/chapter/<chapter_id>/tts/<tts_id>`.
* 
* Example:
*   curl -X GET 127.0.0.1:8000/v2/tts?voice=Will -H 'Authorization: {jwt}'
*   -> {"tts": {
*       "id": "abcdef1234567890",
*       "text": "Hello, world!",
*       "url": "https://s3.amazonaws.com/v1/tts/abcdef1234567890.mp3",
*       "format": "mp3",
*       "duration_ms": 999,
*       "url_expires_at_ms": 1733165254000
*     }}
*/
readonly "getGetTts": (ttsId: string, options?: S.Codec.Encoded<typeof GetGetTtsParams> | undefined) => Effect.Effect<S.Schema.Type<typeof GetGetTts200>, HttpClientError.HttpClientError | ParseError>
  /**
* List all voices
*/
readonly "getListVoices": () => Effect.Effect<S.Schema.Type<typeof GetListVoices200>, HttpClientError.HttpClientError | ParseError>
  /**
* Types of events: https://www.revenuecat.com/docs/event-types-and-fields
* Example event data: https://www.revenuecat.com/docs/sample-events.
*/
readonly "postRevenueCatWebhook": () => Effect.Effect<void, HttpClientError.HttpClientError | ParseError>
}

export interface LegacyClientError<Tag extends string, E> {
  readonly _tag: Tag
  readonly request: HttpClientRequest.HttpClientRequest
  readonly response: HttpClientResponse.HttpClientResponse
  readonly cause: E
}

class LegacyClientErrorImpl extends Data.Error<{
  _tag: string
  cause: any
  request: HttpClientRequest.HttpClientRequest
  response: HttpClientResponse.HttpClientResponse
}> {
  constructor(args: {
    _tag: string
    cause: any
    request: HttpClientRequest.HttpClientRequest
    response: HttpClientResponse.HttpClientResponse
  }) {
    super(args as any)
  }
}

export const LegacyClientError = <Tag extends string, E>(
  tag: Tag,
  cause: E,
  response: HttpClientResponse.HttpClientResponse,
): LegacyClientError<Tag, E> =>
  new LegacyClientErrorImpl({
    _tag: tag,
    cause,
    response,
    request: response.request,
  }) as any
